<template>

	<AppTable
		ref="tables-table"
		:table-headings="tableHeadingsTables"
		:title="getString('tables')"
		:total-items="parsedTables.tables.length"
		:items="currentShownTables"
		:filters="filtersTables"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageTables = v"
		@changed-page-size="v=>pageSizeTables = v"
		@changed-sort="changeSortTables"
		@changed-filter="changeFilterTables"
		allow-download
		@export="exportTables"
		class="mb-30"
	/>

	<AppTable
		ref="rooms-table"
		:table-headings="tableHeadingsRooms"
		:title="getString('rooms')"
		:total-items="parsedTables.rooms.length"
		:items="currentShownRooms"
		:filters="filtersRooms"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageRooms = v"
		@changed-page-size="v=>pageSizeRooms = v"
		@changed-sort="changeSortRooms"
		@changed-filter="changeFilterRooms"
		allow-download
		@export="exportRooms"
		class="mb-30"
	/>

</template>

<script>
import {useCurrencyStore, useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import {useAnalyticsStore} from "@/console-new/stores";
import AppTable from "@/shared/components/AppTable.vue";
import {formatDateOnly} from "@/shared/helpers/dates";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		return {analyticsStore, currencyStore, getString: languageStore.getString, userStore, scriptStore, showNotification: notificationStore.showNotification};
	},
	name: "TablesTablesAggregated",
	components: {AppTable},
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	data() {
		return {
			tableHeadingsRooms: [
				{
					text: this.getString('room'),
					fieldName: 'name',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('orders'),
					fieldName: 'quantity',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('visits'),
					fieldName: 'visits',
					sortable: true,
					cols: 1,
				},
			],
			filtersRooms: [
				{
					type: "quantity",
					key: "quantity",
					text: this.getString('orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "visits",
					text: this.getString('visits'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeRooms: 10,
			currentPageRooms: 1,
			sortFieldRooms: "readable_gross_volume",
			sortAscRooms: null,
			selectedFiltersRooms: null,
			tableHeadingsTables: [
				{
					text: this.getString('table'),
					fieldName: 'name',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('orders'),
					fieldName: 'quantity',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('visits'),
					fieldName: 'visits',
					sortable: true,
					cols: 1,
				},
			],
			filtersTables: [
				{
					type: "quantity",
					key: "quantity",
					text: this.getString('orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "visits",
					text: this.getString('visits'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeTables: 10,
			currentPageTables: 1,
			sortFieldTables: "readable_gross_volume",
			sortAscTables: null,
			selectedFiltersTables: null,
		};
	},
	computed: {
		aggregatedData() {
			return this.analyticsStore.tablesAggregated || {};
		},
		parsedTables() {
			const tables = new Map();
			const rooms = new Map();
			
			// Process orders data
			if (this.aggregatedData.tables_orders) {
				this.aggregatedData.tables_orders
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					})
					.forEach(row => {
						if (row.room_table_id) {
							if (!tables.has(row.room_table_id)) {
								const metadata = this.aggregatedData.tables_metadata?.find(t => t.room_table_id === row.room_table_id);
								tables.set(row.room_table_id, {
									room_table_id: row.room_table_id,
									name: metadata?.table_name || `Table ${row.room_table_id}`,
									quantity: 0,
									gross_volume: 0,
									visits: 0,
								});
							}
							const table = tables.get(row.room_table_id);
							table.quantity += row.orders_cnt || 0;
							table.gross_volume += (row.gross_sales || 0) / 100;
						}
					});
			}
			
			if (this.aggregatedData.rooms_orders) {
				this.aggregatedData.rooms_orders
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					})
					.forEach(row => {
						if (row.room_id) {
							if (!rooms.has(row.room_id)) {
								const metadata = this.aggregatedData.rooms_metadata?.find(r => r.room_id === row.room_id);
								rooms.set(row.room_id, {
									room_id: row.room_id,
									name: metadata?.name || `Room ${row.room_id}`,
									quantity: 0,
									gross_volume: 0,
									visits: 0,
								});
							}
							const room = rooms.get(row.room_id);
							room.quantity += row.orders_cnt || 0;
							room.gross_volume += (row.gross_sales || 0) / 100;
						}
					});
			}
			
			// Process visits data
			if (this.aggregatedData.tables_visits) {
				this.aggregatedData.tables_visits
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					})
					.forEach(row => {
						if (row.room_table_id) {
							if (!tables.has(row.room_table_id)) {
								const metadata = this.aggregatedData.tables_metadata?.find(t => t.room_table_id === row.room_table_id);
								tables.set(row.room_table_id, {
									room_table_id: row.room_table_id,
									name: metadata?.table_name || `Table ${row.room_table_id}`,
									quantity: 0,
									gross_volume: 0,
									visits: 0,
								});
							}
							const table = tables.get(row.room_table_id);
							table.visits += row.visits_count || 0;
						}
					});
			}
			
			if (this.aggregatedData.rooms_visits) {
				this.aggregatedData.rooms_visits
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					})
					.forEach(row => {
						if (row.room_id) {
							if (!rooms.has(row.room_id)) {
								const metadata = this.aggregatedData.rooms_metadata?.find(r => r.room_id === row.room_id);
								rooms.set(row.room_id, {
									room_id: row.room_id,
									name: metadata?.name || `Room ${row.room_id}`,
									quantity: 0,
									gross_volume: 0,
									visits: 0,
								});
							}
							const room = rooms.get(row.room_id);
							room.visits += row.visits_count || 0;
						}
					});
			}
			
			// Convert maps to arrays and add readable values
			let tablesArray = Array.from(tables.values()).map(t => ({
				...t,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(t.gross_volume, false),
			}));
			
			let roomsArray = Array.from(rooms.values()).map(r => ({
				...r,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(r.gross_volume, false),
			}));
			
			// Apply filters and sorting
			tablesArray = this.filterTables(tablesArray);
			roomsArray = this.filterRooms(roomsArray);
			
			if (this.sortFieldTables) {
				let sortField = this.sortFieldTables.startsWith('readable_') ? this.sortFieldTables.replace('readable_', '') : this.sortFieldTables;
				if (!this.sortAscTables) {
					if (sortField === "name") {
						tablesArray.sort((b, a) => a.name.localeCompare(b.name));
					} else {
						tablesArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name") {
						tablesArray.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						tablesArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			if (this.sortFieldRooms) {
				let sortField = this.sortFieldRooms.startsWith('readable_') ? this.sortFieldRooms.replace('readable_', '') : this.sortFieldRooms;
				if (!this.sortAscRooms) {
					if (sortField === "name") {
						roomsArray.sort((b, a) => a.name.localeCompare(b.name));
					} else {
						roomsArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name") {
						roomsArray.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						roomsArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			return {
				tables: tablesArray,
				rooms: roomsArray,
			};
		},
		currentShownTables() {
			return this.parsedTables.tables.slice(
				(this.currentPageTables - 1) * this.pageSizeTables,
				this.currentPageTables * this.pageSizeTables
			);
		},
		currentShownRooms() {
			return this.parsedTables.rooms.slice(
				(this.currentPageRooms - 1) * this.pageSizeRooms,
				this.currentPageRooms * this.pageSizeRooms
			);
		},
	},
	methods: {
		filterRooms(roomsArray) {
			if (!roomsArray || !roomsArray.length || !this.selectedFiltersRooms || !Object.values(this.selectedFiltersRooms).length) {
				return roomsArray;
			}
			for (let filterField in this.selectedFiltersRooms) {
				let filterValue = this.selectedFiltersRooms[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'quantity', 'visits'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						roomsArray = roomsArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						roomsArray = roomsArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						roomsArray = roomsArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return roomsArray;
		},
		filterTables(tablesArray) {
			if (!tablesArray || !tablesArray.length || !this.selectedFiltersTables || !Object.values(this.selectedFiltersTables).length) {
				return tablesArray;
			}
			for (let filterField in this.selectedFiltersTables) {
				let filterValue = this.selectedFiltersTables[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'quantity', 'visits'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						tablesArray = tablesArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						tablesArray = tablesArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						tablesArray = tablesArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return tablesArray;
		},
		changeSortRooms(newSortField, newSortAsc) {
			this.currentPageRooms = 1;
			this.sortFieldRooms = newSortField;
			this.sortAscRooms = !!newSortAsc;
			this.$refs['rooms-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterRooms(newFilters) {
			this.currentPageRooms = 1;
			this.selectedFiltersRooms = newFilters;
			this.$refs['rooms-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeSortTables(newSortField, newSortAsc) {
			this.currentPageTables = 1;
			this.sortFieldTables = newSortField;
			this.sortAscTables = !!newSortAsc;
			this.$refs['tables-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterTables(newFilters) {
			this.currentPageTables = 1;
			this.selectedFiltersTables = newFilters;
			this.$refs['tables-table'].$refs['pagination-btns'].activePage = 1;
		},
		exportTables(expExtension) {
			let nameKey = this.getString('name');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let quantityKey = this.getString('quantity');
			let visitsKey = this.getString('visits');
			let data = this.parsedTables.tables.map(c => {
				let result = {};
				result[nameKey] = c.name;
				result[grossVolumeKey] = Number(c.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[quantityKey] = c.quantity;
				result[visitsKey] = c.visits;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("tables").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("tables"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[nameKey, grossVolumeKey, quantityKey, visitsKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
		exportRooms(expExtension) {
			let nameKey = this.getString('name');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let quantityKey = this.getString('quantity');
			let visitsKey = this.getString('visits');
			let data = this.parsedTables.rooms.map(p => {
				let result = {};
				result[nameKey] = p.name;
				result[grossVolumeKey] = Number(p.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[quantityKey] = p.quantity;
				result[visitsKey] = p.visits;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("rooms").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("rooms"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[nameKey, grossVolumeKey, quantityKey, visitsKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
	},
}
</script>

<style scoped>

</style>